import React, { useEffect, useState } from "react";
import Select from "react-select";
import { DatePicker } from "antd";
import { useSelector } from "react-redux";
import moment from "moment";
import styled, { createGlobalStyle } from "styled-components";
import { Logger } from '../../../utils/logger';

interface TaskFilterProps {
  toggle: () => void;
  modal: boolean;
  filters: any;
  applyFilter: (filterData: any) => void;
}

interface FilterState {
  sortTeamsBy: string;
  sortTeamMemberBy: string;
  sortTaskAssignBy: string;
  orderBy: string;
  taskType: string;
  dateTask: string;
  startDate: moment.Moment | null;
  endDate: moment.Moment | null;
}

// ✅ FIX: Global styles for date picker z-index issues
const GlobalDatePickerStyles = createGlobalStyle`
  .task-filter-date-picker,
  .ant-picker-dropdown,
  .ant-picker-dropdown-placement-bottomLeft,
  .ant-picker-dropdown-placement-topLeft {
    z-index: 99999 !important;
    position: fixed !important;
  }
  
  .ant-picker-panel-container {
    z-index: 99999 !important;
  }
  
  .ant-picker-panel {
    z-index: 99999 !important;
  }
`;

const TaskFilter = ({
  toggle,
  modal,
  filters,
  applyFilter,
  ...args
}: TaskFilterProps) => {
  const [showDatePicker, setShowDatePicker] = useState<boolean>(false);
  const [filterData, setFilterData] = useState<any>({});
  const [startDate, setStartDate] = useState<any>("");
  const [endDate, setEndDate] = useState<any>("");
  const [filterState, setFilterState] = useState<FilterState>({
    sortTeamsBy: "",
    sortTeamMemberBy: "",
    sortTaskAssignBy: "",
    orderBy: "",
    taskType: "",
    dateTask: "",
    startDate: null,
    endDate: null
  });
  const storeState = useSelector((state: any) => {
    return state?.rootReducers;
  });
  const dateCollection = [
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "3DayBefore", label: "3 Days Before Now" },
    { value: "1WeekBefore", label: "1 Week Before Now" },
    { value: "1MonthBefore", label: "1 Month Before Now" },
    { value: "custom", label: "Custom Date" },
  ];

  // 🔒 SECURITY FIX: Filter teams based on subordinate data
  const userRole = storeState?.userData?.userDetails?.role?.name;
  const subordinateData = storeState?.userData?.userDetails?.subordinateEmployees;

  // 📊 COMPREHENSIVE ROLE LOGGING
  Logger.log('🎭 TaskFilter Role Check:', {
    userRole: userRole,
    hasSubordinateData: !!subordinateData,
    subordinateDataKeys: subordinateData ? Object.keys(subordinateData) : []
  });

  const getFilteredTeams = () => {
    Logger.log('🔒 TaskFilter: Applying role-based team filtering', { userRole });

    // Super admin gets all organization teams + organization option
    if (userRole === 'super_admin') {
      const orgTeams = storeState?.userData?.userDetails?.organizationTeams || [];
      const subordinateTeams = subordinateData?.teams || [];

      Logger.log('👑 Super admin team data:', {
        orgTeamsCount: orgTeams.length,
        subordinateTeamsCount: subordinateTeams.length
      });

      // ✅ FIX: If orgTeams is empty but subordinateTeams exists, use subordinateTeams
      const teamsToUse = orgTeams.length > 0 ? orgTeams : subordinateTeams;

      Logger.log('👑 Super admin using teams:', {
        source: orgTeams.length > 0 ? 'organizationTeams' : 'subordinateTeams (fallback)',
        count: teamsToUse.length
      });

      return [
        ...teamsToUse.map((item: any) => ({ value: item?._id, label: item?.name })),
        {
          value: storeState?.userData?.userDetails?.organization?._id,
          label: storeState?.userData?.userDetails?.organization?.name,
        }
      ];
    }

    // 🔒 OTHER ROLES: Only get subordinate teams (NO organization option)
    const subordinateTeams = subordinateData?.teams || [];

    Logger.log('✅ TaskFilter: Filtered teams', {
      role: userRole,
      count: subordinateTeams.length,
      showOrgOption: false
    });

    // Return ONLY subordinate teams (no organization option for non-admins)
    return subordinateTeams.map((item: any) => ({
      value: item?._id,
      label: item?.name
    }));
  };

  const teams = getFilteredTeams();
  const formatName = (firstName: string, lastName: string | null) => {
    if (!firstName) return '';
    return lastName ? `${firstName} ${lastName}`.trim() : firstName.trim();
  };

  // Calculate filtered employees based on role (uses userRole and subordinateData from above)
  const getFilteredEmployees = () => {
    Logger.log('🔒 TaskFilter: Applying role-based employee filtering', { userRole });

    // Super admin gets all organization employees
    if (userRole === 'super_admin') {
      const orgEmployees = storeState?.userData?.userDetails?.organizationEmployee || [];
      const reportTo = subordinateData?.reportTo || [];
      const teamMembers = subordinateData?.teamMembers || [];
      const orgMembers = subordinateData?.organizationMember || [];

      Logger.log('👑 Super admin employee data:', {
        orgEmployeesCount: orgEmployees.length,
        reportToCount: reportTo.length,
        teamMembersCount: teamMembers.length,
        orgMembersCount: orgMembers.length
      });

      // ✅ FIX: If orgEmployees is empty but subordinateData exists, use subordinateData
      if (orgEmployees.length === 0 && (reportTo.length > 0 || teamMembers.length > 0 || orgMembers.length > 0)) {
        const allSubordinates = [...reportTo, ...teamMembers, ...orgMembers];
        // Remove duplicates
        const uniqueEmployees = allSubordinates.filter((user, index, array) =>
          array.findIndex(u => u._id === user._id) === index
        );

        Logger.log('👑 Super admin using subordinate data:', {
          source: 'subordinateEmployees (fallback)',
          count: uniqueEmployees.length
        });

        return uniqueEmployees;
      }

      Logger.log('👑 Super admin using employees:', {
        source: 'organizationEmployee',
        count: orgEmployees.length
      });

      return orgEmployees;
    }

    // Get subordinate arrays
    const reportTo = subordinateData?.reportTo || [];
    const teamMembers = subordinateData?.teamMembers || [];
    const orgMembers = subordinateData?.organizationMember || [];

    Logger.log('📊 Subordinate data breakdown:', {
      reportToCount: reportTo.length,
      teamMembersCount: teamMembers.length,
      orgMembersCount: orgMembers.length
    });

    let filteredUsers: any[] = [];

    if (userRole === 'employee') {
      // 🔒 EMPLOYEES: Can ONLY see themselves
      Logger.log('🔒 Employee: Restricting to self only');
      const currentUser = {
        firstName: storeState?.userData?.userDetails?.firstName || '',
        lastName: storeState?.userData?.userDetails?.lastName || '',
        _id: storeState?.userData?.userDetails?._id,
        email: storeState?.userData?.userDetails?.email || '',
      };
      filteredUsers = [currentUser];

    } else if (userRole === 'manager' || userRole === 'team_lead') {
      // 🔓 MANAGERS/TEAM LEADS: Can see their subordinates
      Logger.log('🔓 Manager/Team Lead: Showing subordinates');
      const allSubordinates = [...reportTo, ...teamMembers];
      // Remove duplicates
      filteredUsers = allSubordinates.filter((user, index, array) =>
        array.findIndex(u => u._id === user._id) === index
      );

    } else {
      // 🔓 OTHER ROLES: Can see organization members
      Logger.log('🔓 Other role: Showing org members');
      const allUsers = [...reportTo, ...teamMembers, ...orgMembers];
      // Remove duplicates
      filteredUsers = allUsers.filter((user, index, array) =>
        array.findIndex(u => u._id === user._id) === index
      );
    }

    Logger.log('✅ TaskFilter: Filtered employees', {
      role: userRole,
      count: filteredUsers.length,
      source: userRole === 'super_admin' ? 'organizationEmployee or fallback' : 'subordinateEmployees'
    });

    return filteredUsers;
  };

  const teamMembers = getFilteredEmployees()?.map(
    (item: any) => {
      return {
        value: item?._id,
        label: formatName(item?.firstName, item?.lastName)
      };
    }
  );

  const taskType = storeState?.user?.userPreferences?.taskType?.map(
    (item: any) => {
      return { value: item?.value, label: item?.name };
    }
  );
  const sortObject = [
    { id: 1, value: "type", label: "Type" },
    { id: 2, value: "toBePerformAt", label: "Due Date" },
    { id: 3, value: "createdBy", label: "Created By" },
  ];

  // Add after state declarations, before dateFilterOnChange:
  const handleFilter = (value: any, name: keyof FilterState) => {
    setFilterState(prev => ({
      ...prev,
      [name]: value
    }));
  };

const getDateRange = (preset: string): { start: moment.Moment, end: moment.Moment } => {
  const end = moment().endOf('day');
  switch (preset) {
    case "today":
      return { 
        start: moment().startOf('day'), 
        end 
      };
    case "yesterday":
      return { 
        start: moment().subtract(1, 'day').startOf('day'),
        end: moment().subtract(1, 'day').endOf('day')
      };
    case "3DayBefore":
      return { 
        start: moment().subtract(3, 'days').startOf('day'),
        end
      };
    case "1WeekBefore":
      return { 
        start: moment().subtract(1, 'week').startOf('day'),
        end
      };
    case "1MonthBefore":
      return { 
        start: moment().subtract(1, 'month').startOf('day'),
        end
      };
    default:
      return { start: moment().startOf('day'), end };
  }
};



const dateFilterOnChange = (preset: string) => {
  Logger.log("Date filter changed to:", preset);
  
  if (preset === "custom") {
    setShowDatePicker(true);
    setFilterState(prev => ({
      ...prev,
      dateTask: preset,
      startDate: null,
      endDate: null
    }));
    return;
  }

  const { start, end } = getDateRange(preset);
  setShowDatePicker(false);
  setFilterState(prev => ({
    ...prev,
    dateTask: preset,
    startDate: start,
    endDate: end
  }));
};

// ✅ SIMPLE FIX in TaskFilter index.tsx:
const handleResetFilter = () => {
  // Reset UI state
  setFilterState({
    sortTeamsBy: "",
    sortTeamMemberBy: "",
    sortTaskAssignBy: "",
    orderBy: "",
    taskType: "",
    dateTask: "",
    startDate: null,
    endDate: null
  });
  setShowDatePicker(false);
  
  // ✅ FIX: Send complete reset
  applyFilter({
    sortTeamsBy: "",
    sortTeamMemberBy: "",
    sortTaskAssignBy: "",
    orderBy: "",
    taskType: "",
    startDate: null,
    endDate: null,
    startedAt: null,
    endedAt: null,
    byOrganization: false
  });
};


 
  
const handleApplyClick = () => {
  const { startDate, endDate, sortTeamsBy } = filterState;
  
  // ✅ FIX: Better validation for custom dates
  if (filterState.dateTask === 'custom') {
    if (!startDate || !endDate) {
      Logger.log("Custom date validation failed - missing dates");
      return;
    }
  }

  // ✅ FIX: Better date formatting for API
  const formattedStartDate = startDate ? 
    moment(startDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : null;
  const formattedEndDate = endDate ? 
    moment(endDate).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : null;

  const filterData = {
    ...filterState,
    startDate: formattedStartDate,
    endDate: formattedEndDate,
    startedAt: formattedStartDate,  // API expects startedAt/endedAt
    endedAt: formattedEndDate,      // API expects startedAt/endedAt
    byOrganization: sortTeamsBy === storeState?.userData?.userDetails?.organization?._id
  };

  Logger.log("Apply filter - dates:", { formattedStartDate, formattedEndDate });
  applyFilter(filterData);
};

  const handleStartDateChange = (date: moment.Moment | null) => {
    setFilterState(prev => ({
      ...prev,
      startDate: date
    }));
  };
  
  const handleEndDateChange = (date: moment.Moment | null) => {
    setFilterState(prev => ({
      ...prev,
      endDate: date
    }));
  };

  const checkTimeDifference = (startD: any = "", endD: any = "") => {
    if (startD && endD) {
      const startMoment = moment(startD);
      const endMoment = moment(endD);
      const today = moment().startOf('day');
      
      // Check if it matches predefined date ranges
      if (startMoment.isSame(today, 'day') && endMoment.isSame(today, 'day')) {
        return "today";
      } else if (startMoment.isSame(moment().subtract(1, 'day'), 'day') && endMoment.isSame(moment().subtract(1, 'day'), 'day')) {
        return "yesterday";
      } else if (startMoment.isSame(moment().subtract(3, 'days').startOf('day'), 'day') && endMoment.isSame(today, 'day')) {
        return "3DayBefore";
      } else if (startMoment.isSame(moment().subtract(1, 'week').startOf('day'), 'day') && endMoment.isSame(today, 'day')) {
        return "1WeekBefore";
      } else if (startMoment.isSame(moment().subtract(1, 'month').startOf('day'), 'day') && endMoment.isSame(today, 'day')) {
        return "1MonthBefore";
      } else {
        // Custom date range
        return "custom";
      }
    } else {
      return "";
    }
  };

  useEffect(() => {
    if (filters && Object.keys(filters).length > 0) {
      const dateTaskValue = checkTimeDifference(filters?.startedAt, filters?.endedAt);
      
      setFilterState(prev => ({
        ...prev,
        sortTeamsBy: filters?.team || "",
        sortTeamMemberBy: filters?.userId || "",
        sortTaskAssignBy: filters?.taskAssign || "",
        orderBy: filters?.orderBy || "",
        taskType: filters?.taskType || "",
        dateTask: dateTaskValue || "",
        // ✅ FIX: Set actual dates for proper calendar display
        startDate: filters?.startedAt ? moment(filters.startedAt) : null,
        endDate: filters?.endedAt ? moment(filters.endedAt) : null
      }));
      
      // ✅ FIX: Only update showDatePicker if we have actual date values
      if (dateTaskValue === "custom" && (filters?.startedAt && filters?.endedAt)) {
        setShowDatePicker(true);
      } else if (dateTaskValue && dateTaskValue !== "custom") {
        setShowDatePicker(false);
      }
      // ✅ FIX: Don't change showDatePicker if no date task value (user might be setting custom)
    }
  }, [filters]);

  // Debug showDatePicker changes (can be removed in production)
  useEffect(() => {
    Logger.log("showDatePicker:", showDatePicker, "dateTask:", filterState.dateTask);
  }, [showDatePicker, filterState.dateTask]);

  return (
    <>
      <GlobalDatePickerStyles />
      <DrawerBox>
        <div>
        <div className="form-group">
          <label className="form-label">Teams</label>
          <select
  id="inputState"
  value={filterState.sortTeamsBy}  // Changed from filterData
  onChange={(e: any) => handleFilter(e.target.value, "sortTeamsBy")}
  name="type"
  className="form-select"
>
            <option value="" disabled={filterData?.sortTeamsBy?.length > 1}>
              Select Team
            </option>
            {teams?.map((task: any, ix: number) => (
              <option value={task?.value} key={ix}>
                {task?.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Team member</label>
          
<select
  id="inputState"
  value={filterState.sortTeamMemberBy}  // Changed from filterData
  onChange={(e: any) => handleFilter(e.target.value, "sortTeamMemberBy")}
  name="type"
  className="form-select"
>
            <option
              value=""
              disabled={filterData?.sortTeamMemberBy?.length > 1}
            >
              Select Team Member
            </option>
            {teamMembers?.map((task: any, ix: number) => (
              <option value={task?.value} key={ix}>
                {task?.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Due Date</label>
          <select
  id="inputState"
  value={filterState.dateTask}  // Changed from filterData
  onChange={(e: any) => dateFilterOnChange(e.target.value)}  // Removed second parameter
  name="type"
  className="form-select"
>
            <option value="" disabled={filterData?.dateTask?.length > 1}>
              Select Date
            </option>
            {dateCollection?.map((data: any, ix: number) => (
              <option value={data?.value} key={ix}>
                {data?.label}
              </option>
            ))}
          </select>
        </div>

     

        {showDatePicker && (
          <div className="form-group">
            <label className="form-label">Custom Dates</label>
            <div className="custom_date_picker">
              <div style={{ marginBottom: '10px' }}>
                <label style={{ fontSize: '12px', marginBottom: '5px', display: 'block' }}>Start Date</label>
                <DatePicker
                  format="DD MMM YYYY"
                  value={filterState.startDate}
                  onChange={handleStartDateChange}
                  placeholder="Select start date"
                  style={{ width: '100%', height: '38px' }}
                  getPopupContainer={() => document.body}
                  popupClassName="task-filter-date-picker"
                  allowClear={true}
                  autoFocus={false}
                  inputReadOnly={false}
                  disabledDate={(current: moment.Moment): boolean => {
                    return current.isAfter(moment());
                  }}
                  onOpenChange={(open) => Logger.log("Start date picker open:", open)}
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', marginBottom: '5px', display: 'block' }}>End Date</label>
                <DatePicker
                  format="DD MMM YYYY"
                  value={filterState.endDate}
                  onChange={handleEndDateChange}
                  placeholder="Select end date"
                  style={{ width: '100%', height: '38px' }}
                  getPopupContainer={() => document.body}
                  popupClassName="task-filter-date-picker"
                  allowClear={true}
                  autoFocus={false}
                  inputReadOnly={false}
                  disabledDate={(current: moment.Moment): boolean => {
                    const startDate = filterState.startDate;
                    return current.isAfter(moment()) || (!!startDate && current.isBefore(startDate));
                  }}
                  onOpenChange={(open) => Logger.log("End date picker open:", open)}
                />
              </div>
            </div>
          </div>
        )}

        <div className="form-group">
          <label className="form-label"> Task assign</label>
          <select
  id="inputState"
  value={filterState.sortTaskAssignBy}  // Changed from filterData
  onChange={(e: any) => handleFilter(e.target.value, "sortTaskAssignBy")}
  name="type"
  className="form-select"
>
            <option
              value=""
              disabled={filterData?.sortTaskAssignBy?.length > 1}
            >
              Select Task assign
            </option>
            {teamMembers?.map((task: any, ix: number) => (
              <option value={task?.value} key={ix}>
                {task?.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Sorting</label>
          <select
  id="inputState"
  value={filterState.orderBy}  // Changed from filterData
  onChange={(e: any) => handleFilter(e.target.value, "orderBy")}
  name="type"
  className="form-select"
>
            <option value="" disabled={filterData?.orderBy?.length > 1}>
              Select Sorting
            </option>
            {sortObject?.map((task: any, key: number) => (
              <option key={key} value={task?.value}>
                {task?.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label"> Task type</label>
          <select
    id="inputState"
    value={filterState.taskType}  // Corrected to taskType
    onChange={(e: any) => handleFilter(e.target.value, "taskType")}  // Corrected to taskType
    name="type"
    className="form-select"
  >
            <option value="" disabled={filterData?.taskType?.length > 1}>
              Select Task type
            </option>
            {taskType?.map((task: any, ix: number) => (
              <option value={task?.value} key={ix}>
                {task?.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="auto_form_btn">
        <button
          type="button"
          className="btn btn-primary-save"
          onClick={() => {
            handleResetFilter();
          }}
        >
          Reset
        </button>
        <button
          type="button"
          onClick={() => handleApplyClick()}
          className="btn btn-primary-save"
        >
          Apply
        </button>
        </div>
      </DrawerBox>
    </>
  );
};

export default TaskFilter;
const DrawerBox = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 30px;
  .form-group {
    margin: 14px 0px;
  }
  .auto_form_btn {
    position: sticky;
    margin-top: auto;
    bottom: 0px;
    display: flex;
    gap: 10px;
  }
  
  /* ✅ FIX: Ensure date picker can render properly */
  .custom_date_picker {
    position: relative;
    z-index: 1000;
    
    .ant-picker {
      height: 38px !important;
      border-radius: 4px !important;
      border: 1px solid #d9d9d9 !important;
      
      &:hover {
        border-color: #40a9ff !important;
      }
      
      &:focus,
      &.ant-picker-focused {
        border-color: #40a9ff !important;
        outline: 0;
        box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2) !important;
      }
    }
    
    .ant-picker-input input {
      cursor: pointer !important;
    }
  }
`;
